import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CalendarRange, Loader2, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Backfill nav_master for a specific date range.
 *
 * Ships alongside NAV Master card so ops can plug NAV gaps left by a
 * stretch of failed scheduler runs (e.g. 18-28 Aug 2026 on production).
 * Calls POST /api/cronjobs/nav-update/backfill-range and polls
 * /nav-update/backfill-status/{job_id} until completion.
 */
export default function NavBackfillRangeCard({ apiUrl }) {
  const today = new Date().toISOString().slice(0, 10);
  const twoWeeksAgo = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(twoWeeksAgo);
  const [toDate, setToDate] = useState(today);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [aumRefreshing, setAumRefreshing] = useState(false);
  const [aumRefreshResult, setAumRefreshResult] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollStatus = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${apiUrl}/api/cronjobs/nav-update/backfill-status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        setStatus(d);
        if (d.status === "completed" || d.status === "failed" || d.status === "cancelled") {
          stopPolling();
          setRunning(false);
          if (d.status === "completed") {
            toast.success(`NAV Backfill done: ${d.dates_with_data || 0}/${d.dates_fetched || 0} dates, ${(d.total_upserted || 0).toLocaleString()} upserts`);
          } else {
            toast.error(`NAV Backfill ${d.status}: ${d.error || "unknown error"}`);
          }
        }
      } catch (_) {
        /* transient, keep polling */
      }
    }, 3000);
  };

  const runBackfill = async () => {
    if (!fromDate || !toDate) {
      toast.error("Pick both From and To dates");
      return;
    }
    if (fromDate > toDate) {
      toast.error("From date must be on or before To date");
      return;
    }
    setRunning(true);
    setStatus(null);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${apiUrl}/api/cronjobs/nav-update/backfill-range`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ from_date: fromDate, to_date: toDate }),
      });
      const d = await r.json();
      if (!r.ok || !d.job_id) {
        throw new Error(d.detail || d.error || "Failed to start backfill");
      }
      toast.info(`NAV Backfill started for ${fromDate} → ${toDate}`);
      pollStatus(d.job_id);
    } catch (e) {
      setRunning(false);
      toast.error(`Could not start backfill: ${e.message || e}`);
    }
  };

  const runAumRefresh = async () => {
    setAumRefreshing(true);
    setAumRefreshResult(null);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${apiUrl}/api/cronjobs/aum-master/refresh-market-values`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        throw new Error(d.detail || d.error || "Refresh failed");
      }
      setAumRefreshResult(d);
      toast.success(
        `AUM NAV refresh done: ${(d.changed || 0).toLocaleString()} rows updated ` +
        `(${d.matched}/${d.scanned} matched, ${d.duration_seconds}s)`
      );
    } catch (e) {
      toast.error(`AUM refresh failed: ${e.message || e}`);
    } finally {
      setAumRefreshing(false);
    }
  };

  const progressPct =
    status && status.progress_total > 0
      ? Math.round(((status.progress_done || 0) / status.progress_total) * 100)
      : 0;

  return (
    <Card className="mb-6 border-sky-200 bg-sky-50/30" data-testid="nav-backfill-range-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sky-700">
          <CalendarRange className="w-5 h-5" />
          Backfill Missing NAVs
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-3">
              Fetches AMFI Historical NAV for every calendar day in the range and upserts into <code className="text-xs bg-white px-1 py-0.5 rounded border">nav_master</code>.
              Use this when the daily 2:30 AM scheduler has been failing for a stretch and you need to catch up.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="text-xs text-gray-500 block mb-1">From Date</label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={running}
                  data-testid="nav-backfill-from-date"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">To Date</label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={running}
                  data-testid="nav-backfill-to-date"
                />
              </div>
            </div>
            {status && (
              <div className="mt-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-gray-500">Progress:</span>
                  <span className="font-medium text-sky-700">
                    {(status.progress_done || 0)} / {(status.progress_total || 0)} dates
                    {progressPct > 0 && ` (${progressPct}%)`}
                  </span>
                  {status.status === "completed" && (
                    <span className="text-emerald-700 ml-2">
                      · {(status.total_upserted || 0).toLocaleString()} NAV records upserted
                    </span>
                  )}
                </div>
                {status.progress_total > 0 && (
                  <div className="h-1.5 bg-gray-200 rounded overflow-hidden max-w-md">
                    <div
                      className="h-full bg-sky-500 transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                )}
                {status.status === "failed" && status.error && (
                  <div className="text-xs text-red-600 mt-1">Error: {status.error}</div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              onClick={runBackfill}
              disabled={running}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="nav-backfill-run-btn"
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Run Backfill
                </>
              )}
            </Button>
            <Button
              onClick={runAumRefresh}
              disabled={aumRefreshing}
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              data-testid="aum-refresh-nav-btn"
              title="Re-source aum_master.current_nav from the fresh scheme_master NAV and recompute market_value across every folio. Idempotent — safe to click any time."
            >
              {aumRefreshing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Refreshing…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh AUM NAV
                </>
              )}
            </Button>
            {aumRefreshResult && (
              <div className="text-xs text-emerald-700 max-w-[180px] leading-tight">
                {(aumRefreshResult.changed || 0).toLocaleString()} rows updated
                <br />
                <span className="text-gray-500">
                  {aumRefreshResult.matched}/{aumRefreshResult.scanned} matched · {aumRefreshResult.duration_seconds}s
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
